import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import {
  getBrandCreationPriorities,
  saveBrandCreationPriorities,
} from "../../lib/brandStudioApi";

const NEEDS = [
  ["identity", "Фирменный стиль", "Логотип, цвета, шрифты и общий язык бренда."],
  ["social", "Соцсети", "Посты, сторис, обложки и система контента."],
  ["business", "Для бизнеса", "Презентации, документы и рабочие материалы."],
  ["client", "Для клиента", "Предложения, инструкции и клиентские материалы."],
  ["print_ads", "Печать и реклама", "Полиграфия, объявления и рекламные носители."],
  ["packaging_physical", "Упаковка", "Упаковка и другие физические носители."],
  ["digital", "Цифровые материалы", "Баннеры, PDF и материалы для экранов."],
  ["website", "Сайт", "Сайт или посадочная страница."],
  ["custom", "Создать своё", "Если нужного варианта нет в списке."],
];

export default function BrandNeedsStepV2({ onContinue = null }) {
  const [status, setStatus] = useState("loading");
  const [selected, setSelected] = useState([]);
  const [customText, setCustomText] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      const current = await getBrandCreationPriorities();
      setSelected(current?.needs || []);
      setCustomText(current?.custom_text || "");
      setSaved(Boolean(current));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = (value) => {
    setSaved(false);
    setSelected((previous) =>
      previous.includes(value)
        ? previous.filter((item) => item !== value)
        : [...previous, value],
    );
  };

  const save = async () => {
    if ((!selected.length && !customText.trim()) || busy) return;
    setBusy(true);
    setError("");
    try {
      const needs =
        customText.trim() && !selected.includes("custom")
          ? [...selected, "custom"]
          : selected;
      const result = await saveBrandCreationPriorities({
        needs,
        custom_text: customText.trim(),
      });
      setSelected(result.needs || []);
      setCustomText(result.custom_text || "");
      setSaved(true);
    } catch {
      setError("Не удалось сохранить выбор. Всё отмеченное осталось на экране.");
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <section className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7">
        <div className="inline-flex items-center gap-2 text-sm text-[#796652]">
          <Loader2 size={16} className="animate-spin" /> Открываю следующий шаг…
        </div>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="rounded-[22px] border border-[#E2CFC8] bg-[#FFF8F4] p-5 md:p-7">
        <h2 className="font-serif text-[28px] text-[#2B211A]">
          Не удалось открыть этот шаг
        </h2>
        <button
          type="button"
          onClick={load}
          className="mt-4 min-h-11 rounded-full border border-[#CDB899] bg-white px-5 text-sm text-[#4A3728]"
        >
          Повторить
        </button>
      </section>
    );
  }

  return (
    <section
      className="rounded-[22px] border border-[#E1D4C2] bg-[#FFFDF9] p-5 md:p-7"
      data-testid="brand-needs-v2"
    >
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-[#8A6D53]">
        ЧТО ТЕБЕ НУЖНО
      </div>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-serif text-[31px] leading-none text-[#2B211A] md:text-[36px]">
            Что особенно нужно сейчас?
          </h2>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-6 text-[#655141]">
            Не нужно выбирать всё. Отметь то, ради чего ты пришла в Brand Studio сейчас.
          </p>
        </div>
        {saved && (
          <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[12px] text-[#586044]">
            <Check size={14} /> Сохранено
          </div>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3">
        {NEEDS.map(([value, label, description]) => {
          const active = selected.includes(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              aria-pressed={active}
              className={
                "min-h-[132px] rounded-[14px] border p-4 text-left transition " +
                (active
                  ? "border-[#8B6B4D] bg-[#F3E9DB]"
                  : "border-[#E1D4C2] bg-white")
              }
              data-testid={"brand-need-" + value}
            >
              <span className="font-serif text-[20px] leading-tight text-[#2B211A]">
                {label}
              </span>
              <span className="mt-2 block text-[11.5px] leading-5 text-[#806B55]">
                {description}
              </span>
            </button>
          );
        })}
      </div>

      {(selected.includes("custom") || customText) && (
        <label className="mt-4 block">
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#927A5B]">
            Что именно хочется создать
          </span>
          <textarea
            value={customText}
            maxLength={1000}
            rows={3}
            onChange={(event) => {
              setCustomText(event.target.value);
              setSaved(false);
            }}
            className="mt-1.5 w-full resize-y rounded-xl border border-[#D8C7AE] bg-white p-3 text-[13.5px] leading-6 text-[#3E2E20] outline-none focus:border-[#8C6C49]"
            data-testid="brand-needs-custom"
          />
        </label>
      )}

      <button
        type="button"
        disabled={(!selected.length && !customText.trim()) || busy}
        onClick={save}
        className="mt-5 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED] disabled:opacity-45"
        data-testid="brand-needs-save"
      >
        {busy ? "Сохраняю…" : "Сохранить выбор"}
      </button>

      {saved && (
        <div className="mt-4">
          <p className="text-[12.5px] leading-5 text-[#806B55]">
            Отлично. Дальше мы будем собирать визуальные ориентиры уже с учётом этих задач.
          </p>
          {onContinue && (
            <button
              type="button"
              onClick={onContinue}
              className="mt-4 min-h-12 rounded-full bg-[#3D2A20] px-6 text-sm text-[#FFF8ED]"
              data-testid="brand-needs-continue"
            >
              Продолжить к визуальным ориентирам
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-[#E2CFC8] bg-[#FFF8F4] p-4 text-[13px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}
    </section>
  );
}
